import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  buscarPlanejamentoCompras,
  calcularPlanejamentoCompras,
  enviarPlanejamentoComprasEmail,
  enviarPlanejamentoComprasWhatsApp,
  getAllBrands,
  getAllCategories,
  getAllSuppliers,
  getEmailStatus,
  getSubCategoriesByCategory,
  getWhatsAppSessao,
} from '../../services/api';
import { getAuthPermissoes } from '../../services/authStorage';
import { useEstabelecimento } from '../../context/EstabelecimentoContext';
import PageHeader from '../common/PageHeader';
import ResponsiveTable from '../common/ResponsiveTable';
import { StatusAtivoChip, sxLinhaInativa } from '../common/StatusAtivo';
import { getApiErrorMessage } from '../../utils/apiError';
import downloadCsv from '../../utils/downloadCsv';
import { normalizeSearchTerm } from '../../utils/productSearch';
import BuscaProdutoCampo from '../common/BuscaProdutoCampo';
import {
  escapeHtml as html,
  loadReportCompany,
  reportBrandHtml,
  REPORT_BRAND_CSS,
} from '../../utils/reportBranding';

const PASSOS = ['Produtos', 'Período e margem', 'Sugestão'];
const PAGE = 20;
const LOTE = 200;
const MAX_LISTA = 1000;

function precisaComprar(item) {
  return Number(item?.sugestao) > 0;
}

function linhaCsv(i) {
  return {
    Codigo: i.codigo,
    Descricao: i.descricao,
    Unidade: i.unidade,
    Fornecedor: i.fornecedorNome || 'Sem fornecedor',
    Grupo: i.categoriaNome || '',
    Subgrupo: i.subCategoriaNome || '',
    Marca: i.marcaNome || '',
    Situacao: precisaComprar(i) ? 'Comprar' : 'Sem compra',
    Saldo: i.saldo,
    Minimo: i.estoqueMinimo,
    Vendido: i.quantidadeVendida,
    'Demanda diaria': i.demandaDiaria,
    Sugestao: i.sugestao,
    'Custo unitario': i.custoUnitario ?? '',
  };
}

function podeRelatorio(permissions) {
  if (permissions == null) return true;
  return Boolean(permissions.estoqueRelatorio || permissions.acessoGerencial);
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function nomeFornecedor(f) {
  return f?.nomeFantasia || f?.razaoSocial || (f?.id ? `Fornecedor ${f.id}` : '');
}

function fmtQtd(n) {
  return Number(n || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 });
}

function fmtMoney(n) {
  if (n == null || n === '') return '—';
  return Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function fmtDataIso(valor) {
  const s = String(valor || '').slice(0, 10);
  const [y, m, d] = s.split('-');
  return d && m && y ? `${d}/${m}/${y}` : s;
}

function imprimirPlanejamento(popup, { company, itens, periodo, filial, cobertura, margem, somenteCompra }) {
  const rows = itens.map((i) => `<tr>
    <td>${html(i.codigo)}</td>
    <td>${html(i.descricao)}</td>
    <td>${html(i.fornecedorNome || 'Sem fornecedor')}</td>
    <td>${html(precisaComprar(i) ? 'Comprar' : 'Sem compra')}</td>
    <td class="right">${html(fmtQtd(i.saldo))}</td>
    <td class="right">${html(fmtQtd(i.quantidadeVendida))}</td>
    <td class="right">${html(fmtQtd(i.sugestao))}</td>
    <td class="right">${html(i.custoUnitario == null ? '—' : fmtMoney(i.custoUnitario))}</td>
  </tr>`).join('');
  const comprar = itens.filter(precisaComprar).length;
  popup.document.open();
  popup.document.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
    <title>Planejamento de compras</title>
    <style>
      @page { size: A4 landscape; margin: 12mm; }
      * { box-sizing: border-box; }
      body { margin: 0; color: #172033; font: 10px Arial, sans-serif; }
      header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #0495f0; padding-bottom: 8px; gap: 16px; }
      h1 { margin: 2px 0 0; font-size: 20px; }
      ${REPORT_BRAND_CSS}
      .period { color: #5f6878; text-align: right; }
      .summary { display: flex; gap: 8px; margin: 12px 0; }
      .summary div { min-width: 130px; border: 1px solid #d8dde7; border-radius: 5px; padding: 7px 9px; }
      .summary span, .summary strong { display: block; }
      .summary span { color: #687386; font-size: 9px; }
      .summary strong { margin-top: 2px; font-size: 13px; }
      table { width: 100%; border-collapse: collapse; }
      thead { display: table-header-group; }
      tr { break-inside: avoid; page-break-inside: avoid; }
      th { background: #eaf6fe; color: #172033; text-align: left; padding: 6px; border-bottom: 1px solid #9fcfec; }
      td { padding: 5px 6px; border-bottom: 1px solid #e2e6ed; vertical-align: top; }
      td.right, th.right { text-align: right; }
      footer { position: fixed; bottom: -7mm; right: 0; color: #737c8c; font-size: 8px; }
      @media screen { body { padding: 18px; } }
    </style></head><body>
    <header>
      ${reportBrandHtml(company, { title: 'Planejamento de compras' })}
      <div class="period">Vendas: ${html(periodo)}<br>Filial: ${html(filial)}<br>Emitido em ${html(new Date().toLocaleString('pt-BR'))}</div>
    </header>
    <section class="summary">
      <div><span>Para comprar</span><strong>${html(comprar)}</strong></div>
      <div><span>Sem necessidade</span><strong>${html(itens.length - comprar)}</strong></div>
      <div><span>Cobertura</span><strong>${html(cobertura)} dias</strong></div>
      <div><span>Margem</span><strong>${html(margem)}%</strong></div>
    </section>
    <div style="color:#5f6878;margin-bottom:8px">${somenteCompra ? 'Somente o que precisa comprar' : 'Lista completa'}</div>
    <table><thead><tr>
      <th>Código</th><th>Descrição</th><th>Fornecedor</th><th>Situação</th>
      <th class="right">Saldo</th><th class="right">Vendido</th><th class="right">Sugestão</th><th class="right">Custo</th>
    </tr></thead><tbody>${rows || '<tr><td colspan="8">Nenhum produto neste relatório.</td></tr>'}</tbody></table>
    <footer>ZenGestão · ${html(itens.length)} registros</footer>
    </body></html>`);
  popup.document.close();
  popup.focus();
  window.setTimeout(() => popup.print(), 300);
}

export default function PlanejamentoComprasPage() {
  const permissions = useMemo(() => getAuthPermissoes(), []);
  const permitido = podeRelatorio(permissions);
  const { estabelecimentoId, estabelecimentos } = useEstabelecimento();

  const hoje = useMemo(() => new Date(), []);
  const inicioPadrao = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return isoDate(d);
  }, []);

  const [passo, setPasso] = useState(0);
  const [termo, setTermo] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [subCategoriaId, setSubCategoriaId] = useState('');
  const [marcaId, setMarcaId] = useState('');
  const [fornecedorId, setFornecedorId] = useState('');
  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [resultados, setResultados] = useState([]);
  const [totalBusca, setTotalBusca] = useState(0);
  const [paginaBusca, setPaginaBusca] = useState(1);
  const [marcados, setMarcados] = useState({});
  const [cesta, setCesta] = useState([]);
  const [paginaCesta, setPaginaCesta] = useState(1);
  const [removerIds, setRemoverIds] = useState({});
  const [buscando, setBuscando] = useState(false);
  const [aviso, setAviso] = useState('');

  const [filialId, setFilialId] = useState('');
  const [historicoInicio, setHistoricoInicio] = useState(inicioPadrao);
  const [historicoFim, setHistoricoFim] = useState(isoDate(hoje));
  const [diasCobertura, setDiasCobertura] = useState('30');
  const [margemPercent, setMargemPercent] = useState('20');
  const [calculando, setCalculando] = useState(false);
  const [itens, setItens] = useState([]);
  const [filtroSituacao, setFiltroSituacao] = useState('todos');
  const [paginaRelatorio, setPaginaRelatorio] = useState(1);
  const [erro, setErro] = useState('');
  const [ok, setOk] = useState('');
  const [waConectado, setWaConectado] = useState(false);
  const [emailAberto, setEmailAberto] = useState(false);
  const [waAberto, setWaAberto] = useState(false);
  const [exportarAberto, setExportarAberto] = useState(false);
  const [formatoExportacao, setFormatoExportacao] = useState('pdf');
  const [escopoExportacao, setEscopoExportacao] = useState('comprar');
  const [destinoEmail, setDestinoEmail] = useState('');
  const [destinoWa, setDestinoWa] = useState('');
  const [somenteEnvio, setSomenteEnvio] = useState(true);
  const [enviando, setEnviando] = useState(false);

  const filiais = useMemo(
    () => (estabelecimentos || []).map((e) => ({
      id: e.id,
      nome: e.nomeFantasia || e.razaoSocial || `Filial ${e.id}`,
    })),
    [estabelecimentos],
  );

  const idsNaCesta = useMemo(() => new Set(cesta.map((p) => p.id)), [cesta]);

  useEffect(() => {
    const load = async () => {
      try {
        const [catResp, marcaResp] = await Promise.all([
          getAllCategories({ indice: 1, qtde: 300 }),
          getAllBrands({ indice: 1, qtde: 300 }),
        ]);
        setCategorias(catResp.data?.items || []);
        setMarcas(marcaResp.data?.items || []);
      } catch {
        /* combos opcionais */
      }
      try {
        const fornResp = await getAllSuppliers({ indice: 1, qtde: 300 });
        const lista = fornResp.data?.items || fornResp.data || [];
        setFornecedores(Array.isArray(lista) ? lista : []);
      } catch {
        setFornecedores([]);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!categoriaId) {
      setSubcategorias([]);
      setSubCategoriaId('');
      return;
    }
    let ativo = true;
    getSubCategoriesByCategory(categoriaId)
      .then(({ data }) => {
        if (!ativo) return;
        const lista = Array.isArray(data) ? data : (data?.items || []);
        setSubcategorias(lista);
      })
      .catch(() => {
        if (ativo) setSubcategorias([]);
      });
    return () => { ativo = false; };
  }, [categoriaId]);

  useEffect(() => {
    if (!filialId && estabelecimentoId) setFilialId(String(estabelecimentoId));
  }, [estabelecimentoId, filialId]);

  useEffect(() => {
    const id = Number(filialId) || Number(estabelecimentoId);
    if (!id) {
      setWaConectado(false);
      return undefined;
    }
    let ativo = true;
    getWhatsAppSessao(id)
      .then(({ data }) => {
        if (!ativo) return;
        setWaConectado(data?.configurado !== false && data?.status === 'conectado');
      })
      .catch(() => {
        if (ativo) setWaConectado(false);
      });
    return () => { ativo = false; };
  }, [filialId, estabelecimentoId]);

  const filtrosBusca = () => ({
    termo: normalizeSearchTerm(termo),
    categoriaId: categoriaId ? Number(categoriaId) : null,
    subCategoriaId: subCategoriaId ? Number(subCategoriaId) : null,
    marcaId: marcaId ? Number(marcaId) : null,
    fornecedorId: fornecedorId ? Number(fornecedorId) : null,
  });

  const temFiltro = () => {
    const f = filtrosBusca();
    return Boolean(f.termo || f.categoriaId || f.subCategoriaId || f.marcaId || f.fornecedorId);
  };

  const incorporar = (lista) => {
    setCesta((atual) => {
      const vistos = new Set(atual.map((p) => p.id));
      const novos = lista.filter((p) => p?.id && !vistos.has(p.id));
      return novos.length ? [...atual, ...novos] : atual;
    });
  };

  const buscar = async (pagina = 1) => {
    if (!permitido) {
      setErro('Sem permissão para relatórios de estoque.');
      return [];
    }
    setBuscando(true);
    setErro('');
    try {
      const { data } = await buscarPlanejamentoCompras({
        ...filtrosBusca(),
        indice: pagina,
        qtde: PAGE,
      });
      const lista = data?.items || [];
      setResultados(lista);
      setTotalBusca(data?.total || lista.length);
      setPaginaBusca(pagina);
      setMarcados({});
      return lista;
    } catch (e) {
      setErro(getApiErrorMessage(e, 'Não foi possível buscar os produtos.'));
      return [];
    } finally {
      setBuscando(false);
    }
  };

  const adicionarFiltrados = async () => {
    if (!temFiltro()) {
      setErro('Informe um produto, grupo, subgrupo, marca ou fornecedor antes de adicionar todos.');
      return;
    }
    if (!permitido) {
      setErro('Sem permissão para relatórios de estoque.');
      return;
    }
    setBuscando(true);
    setErro('');
    setAviso('');
    try {
      const acumulado = [];
      let total = 0;
      let pagina = 1;
      while (acumulado.length < MAX_LISTA) {
        const { data } = await buscarPlanejamentoCompras({
          ...filtrosBusca(),
          indice: pagina,
          qtde: LOTE,
        });
        const lista = data?.items || [];
        total = data?.total || lista.length;
        acumulado.push(...lista);
        if (!lista.length || acumulado.length >= total) break;
        pagina += 1;
      }
      const cortados = acumulado.slice(0, MAX_LISTA);
      incorporar(cortados);
      setPaginaCesta(1);
      setRemoverIds({});
      if (!total) {
        setAviso('Nenhum produto ativo nesse filtro.');
      } else if (total > MAX_LISTA) {
        setAviso(`O filtro tem ${total} produtos. Entraram os primeiros ${MAX_LISTA}. Marque na lista o que não entra e remova.`);
      } else {
        setAviso(`${cortados.length} produto(s) adicionados. Marque na lista o que não entra e remova.`);
      }
      await buscar(1);
    } catch (e) {
      setErro(getApiErrorMessage(e, 'Não foi possível adicionar os produtos do filtro.'));
    } finally {
      setBuscando(false);
    }
  };

  const removerSelecionados = () => {
    const ids = new Set(Object.entries(removerIds).filter(([, on]) => on).map(([id]) => Number(id)));
    if (!ids.size) return;
    setCesta((lista) => {
      const proxima = lista.filter((p) => !ids.has(p.id));
      const paginas = Math.max(1, Math.ceil(proxima.length / PAGE));
      setPaginaCesta((atual) => Math.min(atual, paginas));
      return proxima;
    });
    setRemoverIds({});
  };

  const calcular = async () => {
    if (!cesta.length) {
      setErro('Adicione ao menos um produto.');
      setPasso(0);
      return;
    }
    setCalculando(true);
    setErro('');
    try {
      const { data } = await calcularPlanejamentoCompras({
        produtoIds: cesta.map((p) => p.id),
        estabelecimentoId: filialId ? Number(filialId) : null,
        historicoInicio,
        historicoFim,
        diasCobertura: Number(diasCobertura) || 30,
        margemPercent: Number(String(margemPercent).replace(',', '.')) || 0,
      });
      setItens(data?.items || []);
      setFiltroSituacao('todos');
      setPaginaRelatorio(1);
      setPasso(2);
    } catch (e) {
      setErro(getApiErrorMessage(e, 'Não foi possível calcular a sugestão.'));
    } finally {
      setCalculando(false);
    }
  };

  const exportar = (somenteCompra) => {
    const linhas = (somenteCompra ? itens.filter(precisaComprar) : itens).map(linhaCsv);
    if (!linhas.length) {
      setErro(somenteCompra
        ? 'Nenhum produto com quantidade a comprar.'
        : 'Nenhum produto para exportar.');
      return;
    }
    downloadCsv(somenteCompra ? 'planejamento-compras.csv' : 'planejamento-completo.csv', linhas);
  };

  const exportarPdf = async (somenteCompra) => {
    const lista = somenteCompra ? itens.filter(precisaComprar) : itens;
    if (!lista.length) {
      setErro(somenteCompra
        ? 'Nenhum produto com quantidade a comprar.'
        : 'Nenhum produto para o PDF.');
      return;
    }
    const popup = window.open('', '_blank', 'width=1100,height=760');
    if (!popup) {
      setErro('O navegador bloqueou a geração do PDF. Libere pop-ups para este site.');
      return;
    }
    popup.document.write('<!doctype html><title>Gerando relatório</title><p style="font:16px Arial;padding:24px">Preparando relatório…</p>');
    try {
      const company = await loadReportCompany();
      const filialNome = filialId
        ? (filiais.find((f) => String(f.id) === String(filialId))?.nome || 'Filial')
        : 'Todas as filiais';
      imprimirPlanejamento(popup, {
        company,
        itens: lista,
        periodo: `${fmtDataIso(historicoInicio)} a ${fmtDataIso(historicoFim)}`,
        filial: filialNome,
        cobertura: diasCobertura,
        margem: margemPercent,
        somenteCompra,
      });
    } catch (e) {
      popup.close();
      setErro(getApiErrorMessage(e, 'Não foi possível gerar o PDF.'));
    }
  };

  const confirmarExportacao = () => {
    const somente = escopoExportacao === 'comprar';
    setExportarAberto(false);
    if (formatoExportacao === 'csv') exportar(somente);
    else exportarPdf(somente);
  };

  const payloadEnvio = (somenteCompra) => ({
    produtoIds: cesta.map((p) => p.id),
    estabelecimentoId: filialId ? Number(filialId) : null,
    canalEstabelecimentoId: Number(filialId) || Number(estabelecimentoId) || null,
    historicoInicio,
    historicoFim,
    diasCobertura: Number(diasCobertura) || 30,
    margemPercent: Number(String(margemPercent).replace(',', '.')) || 0,
    somenteCompra,
  });

  const enviarEmail = async () => {
    const email = destinoEmail.trim();
    if (!email.includes('@')) {
      setErro('Informe um e-mail válido.');
      return;
    }
    setEnviando(true);
    setErro('');
    try {
      const { data: status } = await getEmailStatus();
      if (!status?.conectado) {
        setErro('Nenhum e-mail configurado para este estabelecimento.');
        return;
      }
      await enviarPlanejamentoComprasEmail({ ...payloadEnvio(somenteEnvio), email });
      setEmailAberto(false);
      setOk(`PDF enviado para ${email}.`);
    } catch (e) {
      setErro(getApiErrorMessage(e, 'Não foi possível enviar o e-mail.'));
    } finally {
      setEnviando(false);
    }
  };

  const enviarWhatsApp = async () => {
    const tel = destinoWa.replace(/\D/g, '');
    if (tel.length < 10) {
      setErro('Informe um WhatsApp com DDD.');
      return;
    }
    setEnviando(true);
    setErro('');
    try {
      await enviarPlanejamentoComprasWhatsApp({ ...payloadEnvio(somenteEnvio), telefone: destinoWa });
      setWaAberto(false);
      setOk('PDF enviado pelo WhatsApp.');
    } catch (e) {
      setErro(getApiErrorMessage(e, 'Não foi possível enviar o WhatsApp.'));
    } finally {
      setEnviando(false);
    }
  };

  const visiveisMarcados = resultados.filter((p) => marcados[p.id] && !idsNaCesta.has(p.id));
  const paginasBusca = Math.max(1, Math.ceil(totalBusca / PAGE));
  const paginasCesta = Math.max(1, Math.ceil(cesta.length / PAGE));
  const paginaCestaAtual = Math.min(paginaCesta, paginasCesta);
  const cestaPagina = cesta.slice((paginaCestaAtual - 1) * PAGE, paginaCestaAtual * PAGE);
  const idsRemoverPagina = cestaPagina.filter((p) => removerIds[p.id]);
  const todosRemoverPagina = cestaPagina.length > 0 && idsRemoverPagina.length === cestaPagina.length;

  const itensFiltrados = itens.filter((i) => {
    if (filtroSituacao === 'comprar') return precisaComprar(i);
    if (filtroSituacao === 'sem') return !precisaComprar(i);
    return true;
  });
  const qtdComprar = itens.filter(precisaComprar).length;
  const paginasRelatorio = Math.max(1, Math.ceil(itensFiltrados.length / PAGE));
  const relatorioPagina = itensFiltrados.slice((paginaRelatorio - 1) * PAGE, paginaRelatorio * PAGE);

  return (
    <Box>
      <PageHeader
        title="Planejamento de compras"
        subtitle="Monte a lista, escolha o período e veja a quantidade sugerida a partir das vendas."
        actions={passo === 2 ? (
          <>
            <Button
              startIcon={<DownloadIcon />}
              variant="contained"
              disabled={!itens.length}
              onClick={() => {
                setFormatoExportacao('pdf');
                setEscopoExportacao(qtdComprar ? 'comprar' : 'todos');
                setExportarAberto(true);
              }}
            >
              Exportar
            </Button>
            <Button startIcon={<EmailOutlinedIcon />} variant="outlined" onClick={() => { setSomenteEnvio(true); setEmailAberto(true); }} disabled={!itens.length}>
              E-mail
            </Button>
            {waConectado && (
              <Button startIcon={<WhatsAppIcon />} variant="outlined" onClick={() => { setSomenteEnvio(true); setWaAberto(true); }} disabled={!itens.length}>
                WhatsApp
              </Button>
            )}
          </>
        ) : null}
      />

      <Stepper activeStep={passo} sx={{ mb: 3 }}>
        {PASSOS.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')}>{ok}</Alert>}
      {aviso && passo === 0 && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setAviso('')}>{aviso}</Alert>}

      {passo === 0 && (
        <Stack spacing={2}>
          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              <BuscaProdutoCampo
                value={termo}
                onChange={setTermo}
                onSearch={() => buscar(1)}
                fullWidth
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  select
                  label="Grupo"
                  value={categoriaId}
                  onChange={(e) => { setCategoriaId(e.target.value); setSubCategoriaId(''); }}
                  fullWidth
                >
                  <MenuItem value="">Todos</MenuItem>
                  {categorias.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>{c.descricao}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Subgrupo"
                  value={subCategoriaId}
                  onChange={(e) => setSubCategoriaId(e.target.value)}
                  disabled={!categoriaId}
                  fullWidth
                >
                  <MenuItem value="">Todos</MenuItem>
                  {subcategorias.map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>{s.descricao}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Marca"
                  value={marcaId}
                  onChange={(e) => setMarcaId(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="">Todas</MenuItem>
                  {marcas.map((m) => (
                    <MenuItem key={m.id} value={String(m.id)}>{m.descricao}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Fornecedor padrão"
                  value={fornecedorId}
                  onChange={(e) => setFornecedorId(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="">Todos</MenuItem>
                  {fornecedores.map((f) => (
                    <MenuItem key={f.id} value={String(f.id)}>{nomeFornecedor(f)}</MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button variant="contained" onClick={() => buscar(1)} disabled={buscando || !permitido}>
                  Buscar
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => incorporar(visiveisMarcados)}
                  disabled={!visiveisMarcados.length}
                >
                  Adicionar selecionados
                </Button>
                <Button variant="outlined" onClick={adicionarFiltrados} disabled={buscando || !permitido}>
                  Adicionar filtrados
                </Button>
              </Stack>
              {totalBusca > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {totalBusca} produto(s) no filtro. A lista abaixo mostra uma página; use Adicionar filtrados para trazer o grupo e depois remova o que não entra.
                </Typography>
              )}
            </Stack>
          </Paper>

          <ResponsiveTable>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Código</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Grupo</TableCell>
                  <TableCell>Fornecedor</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {resultados.map((p) => {
                  const ja = idsNaCesta.has(p.id);
                  return (
                    <TableRow key={p.id} hover selected={ja}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={ja || Boolean(marcados[p.id])}
                          disabled={ja}
                          onChange={(e) => setMarcados((m) => ({ ...m, [p.id]: e.target.checked }))}
                        />
                      </TableCell>
                      <TableCell>{p.codigo}</TableCell>
                      <TableCell>{p.descricao}</TableCell>
                      <TableCell>{p.categoriaNome}</TableCell>
                      <TableCell>{p.fornecedorNome || 'Sem fornecedor'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title={ja ? 'Já está na lista' : 'Adicionar'}>
                          <span>
                            <IconButton size="small" disabled={ja} onClick={() => incorporar([p])} aria-label="Adicionar">
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!resultados.length && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        Busque e adicione os produtos que entram no planejamento.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ResponsiveTable>
          {paginasBusca > 1 && (
            <Stack direction="row" justifyContent="flex-end">
              <Pagination
                count={paginasBusca}
                page={paginaBusca}
                onChange={(_, v) => buscar(v)}
                color="primary"
                disabled={buscando}
              />
            </Stack>
          )}

          <Paper sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1 }}>
              <Typography fontWeight={700}>
                Lista ({cesta.length})
              </Typography>
              <Button
                color="error"
                variant="outlined"
                startIcon={<DeleteOutlineIcon />}
                disabled={!Object.values(removerIds).some(Boolean)}
                onClick={removerSelecionados}
              >
                Remover selecionados
              </Button>
            </Stack>
            <ResponsiveTable>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={todosRemoverPagina}
                        indeterminate={idsRemoverPagina.length > 0 && !todosRemoverPagina}
                        disabled={!cestaPagina.length}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setRemoverIds((atual) => {
                            const proximo = { ...atual };
                            cestaPagina.forEach((p) => { proximo[p.id] = on; });
                            return proximo;
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell>Código</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Fornecedor</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cestaPagina.map((p) => (
                    <TableRow key={p.id} hover selected={Boolean(removerIds[p.id])}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={Boolean(removerIds[p.id])}
                          onChange={(e) => setRemoverIds((m) => ({ ...m, [p.id]: e.target.checked }))}
                        />
                      </TableCell>
                      <TableCell>{p.codigo}</TableCell>
                      <TableCell>{p.descricao}</TableCell>
                      <TableCell>{p.fornecedorNome || 'Sem fornecedor'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          aria-label="Remover"
                          onClick={() => {
                            setCesta((lista) => lista.filter((x) => x.id !== p.id));
                            setRemoverIds((m) => ({ ...m, [p.id]: false }));
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!cesta.length && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography color="text.secondary">Nenhum produto na lista.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ResponsiveTable>
            {paginasCesta > 1 && (
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                <Pagination
                  count={paginasCesta}
                  page={paginaCestaAtual}
                  onChange={(_, v) => setPaginaCesta(v)}
                  color="primary"
                />
              </Stack>
            )}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" disabled={!cesta.length} onClick={() => { setErro(''); setPasso(1); }}>
                Continuar
              </Button>
            </Box>
          </Paper>
        </Stack>
      )}

      {passo === 1 && (
        <Paper sx={{ p: 2 }}>
          <Stack spacing={2} maxWidth={560}>
            <TextField
              select
              label="Filial"
              value={filialId}
              onChange={(e) => setFilialId(e.target.value)}
              helperText="Todas soma saldo e vendas das filiais permitidas."
            >
              <MenuItem value="">Todas</MenuItem>
              {filiais.map((f) => (
                <MenuItem key={f.id} value={String(f.id)}>{f.nome}</MenuItem>
              ))}
            </TextField>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Vendas de"
                type="date"
                value={historicoInicio}
                onChange={(e) => setHistoricoInicio(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="até"
                type="date"
                value={historicoFim}
                onChange={(e) => setHistoricoFim(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
            <TextField
              label="Dias de cobertura"
              type="number"
              value={diasCobertura}
              onChange={(e) => setDiasCobertura(e.target.value)}
              helperText="Para quantos dias a compra deve durar."
              inputProps={{ min: 1, max: 365 }}
            />
            <TextField
              label="Margem de segurança (%)"
              type="number"
              value={margemPercent}
              onChange={(e) => setMargemPercent(e.target.value)}
              inputProps={{ min: 0, max: 500, step: '0.1' }}
            />
            <Typography variant="body2" color="text.secondary">
              A sugestão usa a média diária do período, multiplica pelos dias de cobertura e pela margem, e desconta o saldo atual. Sem venda no período, a sugestão fica zero, a menos que o saldo esteja abaixo do mínimo.
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => setPasso(0)}>Voltar</Button>
              <Button variant="contained" onClick={calcular} disabled={calculando || !permitido}>
                Calcular sugestão
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {passo === 2 && (
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
            <Typography color="text.secondary">
              {qtdComprar} para comprar · {itens.length - qtdComprar} sem necessidade
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={filtroSituacao}
              onChange={(_, valor) => {
                if (!valor) return;
                setFiltroSituacao(valor);
                setPaginaRelatorio(1);
              }}
            >
              <ToggleButton value="todos">Todos</ToggleButton>
              <ToggleButton value="comprar">Comprar</ToggleButton>
              <ToggleButton value="sem">Sem compra</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <ResponsiveTable>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Fornecedor</TableCell>
                  <TableCell>Situação</TableCell>
                  <TableCell align="right">Saldo</TableCell>
                  <TableCell align="right">Vendido</TableCell>
                  <TableCell align="right">Sugestão</TableCell>
                  <TableCell align="right">Custo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {relatorioPagina.map((i) => {
                  const comprar = precisaComprar(i);
                  return (
                    <TableRow key={i.produtoId} hover sx={sxLinhaInativa(comprar)}>
                      <TableCell>{i.codigo}</TableCell>
                      <TableCell>{i.descricao}</TableCell>
                      <TableCell>{i.fornecedorNome || 'Sem fornecedor'}</TableCell>
                      <TableCell>
                        <StatusAtivoChip ativo={comprar} labelAtivo="Comprar" labelInativo="Sem compra" />
                      </TableCell>
                      <TableCell align="right">{fmtQtd(i.saldo)}</TableCell>
                      <TableCell align="right">{fmtQtd(i.quantidadeVendida)}</TableCell>
                      <TableCell align="right">{fmtQtd(i.sugestao)}</TableCell>
                      <TableCell align="right">{fmtMoney(i.custoUnitario)}</TableCell>
                    </TableRow>
                  );
                })}
                {!relatorioPagina.length && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        Nenhum produto neste filtro.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ResponsiveTable>
          {paginasRelatorio > 1 && (
            <Stack direction="row" justifyContent="flex-end">
              <Pagination
                count={paginasRelatorio}
                page={Math.min(paginaRelatorio, paginasRelatorio)}
                onChange={(_, v) => setPaginaRelatorio(v)}
                color="primary"
              />
            </Stack>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setPasso(1)}>Ajustar período</Button>
          </Box>
        </Stack>
      )}

      <Dialog open={exportarAberto} onClose={() => setExportarAberto(false)} fullWidth maxWidth="xs">
        <DialogTitle>Exportar planejamento</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Formato</Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={formatoExportacao}
                onChange={(_, valor) => { if (valor) setFormatoExportacao(valor); }}
              >
                <ToggleButton value="pdf">PDF</ToggleButton>
                <ToggleButton value="csv">CSV</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Conteúdo</Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={escopoExportacao}
                onChange={(_, valor) => { if (valor) setEscopoExportacao(valor); }}
              >
                <ToggleButton value="todos">Todos</ToggleButton>
                <ToggleButton value="comprar" disabled={!qtdComprar}>Só comprar</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportarAberto(false)}>Cancelar</Button>
          <Button variant="contained" onClick={confirmarExportacao}>Exportar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={emailAberto} onClose={() => !enviando && setEmailAberto(false)} fullWidth maxWidth="sm">
        <DialogTitle>Enviar planejamento por e-mail</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="E-mail"
              type="email"
              fullWidth
              autoFocus
              value={destinoEmail}
              onChange={(e) => setDestinoEmail(e.target.value)}
            />
            <FormControlLabel
              control={<Checkbox checked={somenteEnvio} onChange={(e) => setSomenteEnvio(e.target.checked)} />}
              label="Enviar apenas o que precisa comprar"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailAberto(false)} disabled={enviando}>Cancelar</Button>
          <Button variant="contained" onClick={enviarEmail} disabled={enviando}>Enviar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={waAberto} onClose={() => !enviando && setWaAberto(false)} fullWidth maxWidth="sm">
        <DialogTitle>Enviar planejamento por WhatsApp</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="WhatsApp (com DDD)"
              fullWidth
              autoFocus
              value={destinoWa}
              onChange={(e) => setDestinoWa(e.target.value)}
            />
            <FormControlLabel
              control={<Checkbox checked={somenteEnvio} onChange={(e) => setSomenteEnvio(e.target.checked)} />}
              label="Enviar apenas o que precisa comprar"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaAberto(false)} disabled={enviando}>Cancelar</Button>
          <Button variant="contained" onClick={enviarWhatsApp} disabled={enviando}>Enviar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
